import { c as _c } from "react/compiler-runtime";
import { Form, Formik } from 'formik';
import { useContext, useState } from 'react';
import { useTheme } from 'styled-components';
import { GUIDED_CONNECTION_WIDTHS } from '@hookdeck/theme';
import { APIDestination } from '../../../../../../../../typings/Destination.interface';
import { DestinationType } from '../../../../../../../../typings/Integration.interface';
import { cleanseFormErrorObject } from '../../../../../utils/form';
import Badge from '../../../../common/base/Badge';
import Button from '../../../../common/base/Button';
import { StyledCard } from '../../../../common/base/Card';
import Icon, { IconName } from '../../../../common/base/Icon';
import Text from '../../../../common/base/Text';
import { StyledRadioCard } from '../../../../common/Form/Fields/RadioCardsInput';
import { Div } from '../../../../common/helpers/StyledUtils';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import configuration_form_props, { DestinationConfigurationFormValues } from '../../Connections/Forms/destination_configuration';
import resource_details_form_props from '../../Connections/Forms/resource_details';
import { useToasts } from '../../../../common/Toast';
import { DashboardContext } from '../../DashboardContext';
const DESTINATION_TYPE_OPTIONS: {
  value: DestinationType;
  badge?: string;
  description: string;
  image: string;
  bullets: {
    icon: IconName;
    text: string;
  }[];
}[] = [{
  value: 'HTTP',
  description: 'Send events to any public HTTP endpoint, like your API or a third-party service.',
  image: 'destination-type-http',
  bullets: [{
    icon: 'environment',
    text: 'Use in any environment'
  }, {
    icon: 'code',
    text: 'Triggers real application logic'
  }, {
    icon: 'history',
    text: 'Automatic retries with durable queueing'
  }]
}, {
  value: 'MOCK_API',
  badge: 'Testing',
  description: 'Observe inbound HTTP requests before connecting your API or server.',
  image: 'destination-type-mock-api',
  bullets: [{
    icon: 'inspect',
    text: 'Inspect webhooks without an API or server'
  }, {
    icon: 'test',
    text: 'Test filters and transformations'
  }, {
    icon: 'done_all',
    text: 'Validate Hookdeck behavior end-to-end'
  }]
}, {
  value: 'CLI',
  badge: 'Local',
  description: 'Forward events to your local machine via the Hookdeck CLI.',
  image: 'destination-type-cli',
  bullets: [{
    icon: 'terminal',
    text: 'Receive webhooks to a local server'
  }, {
    icon: 'local',
    text: 'Replay and inspect event data locally'
  }, {
    icon: 'flow',
    text: 'Use in CI to test event flows'
  }]
}];
const DESTINATION_TYPE_CARDS_WIDTH = GUIDED_CONNECTION_WIDTHS.step_container + GUIDED_CONNECTION_WIDTHS.details_container + 24;
const DestinationTypeCards: React.FC<{
  selected_type: DestinationType;
  onSelect: (type: DestinationType) => void;
}> = t0 => {
  const $ = _c(8);
  const {
    selected_type,
    onSelect
  } = t0;
  const {
    destination_types
  } = useContext(DashboardContext);
  const theme = useTheme();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      display: "grid",
      gap: "16px",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      width: `${DESTINATION_TYPE_CARDS_WIDTH}px`
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== destination_types || $[2] !== onSelect || $[3] !== selected_type || $[4] !== theme) {
    t2 = DESTINATION_TYPE_OPTIONS.map(option => {
      const destination_type = destination_types?.[option.value];
      const label = destination_type?.label || option.value;
      const is_checked = selected_type === option.value;
      return <StyledRadioCard as="label" key={option.value} checked={is_checked} data-testid={`guided-destination-type-${option.value}`} flex={{
        direction: "column"
      }} style={{
        backgroundColor: theme.colors.surface.base.surface
      }}><Div p={{
          x: 4,
          t: 4,
          b: 5
        }}><Div flex={{
            align: "flex-start",
            gap: 4
          }}><input style={{
              marginTop: "2px"
            }} type="radio" name="type" value={option.value} checked={is_checked} onChange={() => onSelect(option.value)} /><Div style={{
              flex: 1,
              minWidth: 0
            }}><Div flex={{
                align: "center",
                gap: 3
              }}><Text semi={true}>{label}</Text>{option.badge && <Badge neutral={true}>{option.badge}</Badge>}</Div><Text muted={true} size="m" m={{
                t: 1
              }}>{option.description}</Text><Div m={{
                t: 4
              }} flex={{
                direction: "column",
                gap: 2
              }}>{option.bullets.map(_temp)}</Div></Div></Div></Div><Div flex={true} p={{
          x: 2,
          b: 2
        }} style={{
          marginTop: "auto"
        }}><StyledCard overflow_hidden={true} flex={{
            align: "center",
            justify: "center"
          }} style={{
            backgroundColor: theme.colors.surface.base.background,
            minHeight: "176px",
            width: "100%"
          }}><img src={`/images/guided-connection/${option.image}-${theme.mode}.png`} alt={label} style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain"
            }} /></StyledCard></Div></StyledRadioCard>;
    });
    $[1] = destination_types;
    $[2] = onSelect;
    $[3] = selected_type;
    $[4] = theme;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <Div role="radiogroup" style={t1}>{t2}</Div>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
export const SelectDestinationType: React.FC<{
  context: {
    destination: APIDestination | null;
    selected_destination_type: DestinationType;
    preview_destination_type: DestinationType;
    destination_type_committed: boolean;
  };
  mutateContext: (input: {
    selected_destination_type?: DestinationType;
    preview_destination_type?: DestinationType;
    destination_type_committed?: boolean;
  }) => void;
  nextStep: () => void;
  is_current_step: boolean;
}> = t0 => {
  const $ = _c(24);
  const {
    context: t1,
    mutateContext,
    nextStep,
    is_current_step
  } = t0;
  const {
    selected_destination_type,
    preview_destination_type,
    destination_type_committed
  } = t1;
  const [selected_type, setSelectedType] = useState(destination_type_committed ? selected_destination_type : preview_destination_type);
  const has_changed = selected_type !== selected_destination_type;
  let t2;
  if ($[0] !== destination_type_committed || $[1] !== mutateContext) {
    t2 = type => {
      setSelectedType(type);
      if (!destination_type_committed) {
        mutateContext({
          preview_destination_type: type
        });
      }
    };
    $[0] = destination_type_committed;
    $[1] = mutateContext;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const selectDestinationType = t2;
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      b: 6
    };
    t4 = {
      px: GUIDED_CONNECTION_WIDTHS.step_container
    };
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Div m={t3} w={t4}><Text bold={true} size="l" m={{
        b: 0
      }} flex={{
        align: "center"
      }}>Where should your events land?</Text><Text as="p" muted={true}>When HTTP requests hit Hookdeck, the platform queues an outgoing event to a destination of the following types. Select which you’d like to use below.</Text></Div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== selectDestinationType || $[7] !== selected_type) {
    t6 = <DestinationTypeCards selected_type={selected_type} onSelect={selectDestinationType} />;
    $[6] = selectDestinationType;
    $[7] = selected_type;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      gap: 3
    };
    t8 = {
      t: 8
    };
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  const t9 = !is_current_step && !has_changed;
  const t10 = !is_current_step;
  let t11;
  if ($[11] !== mutateContext || $[12] !== nextStep || $[13] !== selected_type) {
    t11 = () => {
      mutateContext({
        selected_destination_type: selected_type,
        preview_destination_type: selected_type,
        destination_type_committed: true
      });
      nextStep();
    };
    $[11] = mutateContext;
    $[12] = nextStep;
    $[13] = selected_type;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const t12 = is_current_step ? "Next" : "Update";
  let t13;
  if ($[15] !== is_current_step || $[16] !== t10 || $[17] !== t11 || $[18] !== t12 || $[19] !== t9) {
    t13 = <Div flex={t7} m={t8}><Button data-testid="guided-select-destination-type-next" disabled={t9} primary={is_current_step} outline={t10} onClick={t11}>{t12}</Button></Div>;
    $[15] = is_current_step;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
    $[19] = t9;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t13 || $[22] !== t6) {
    t14 = <Div data-testid="guided-select-destination-type-form">{t5}{t6}{t13}</Div>;
    $[21] = t13;
    $[22] = t6;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  return t14;
};
const AddDestination: React.FC<{
  context: {
    destination: APIDestination | null;
    selected_destination_type: DestinationType;
    preview_destination_type: DestinationType;
    destination_type_committed: boolean;
  };
  mutateContext: (any) => void;
  nextStep: () => void;
}> = t0 => {
  const $ = _c(27);
  const {
    context: t1,
    mutateContext,
    nextStep
  } = t0;
  const {
    destination,
    selected_destination_type,
    preview_destination_type,
    destination_type_committed
  } = t1;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  const destination_type = destination_type_committed ? selected_destination_type : preview_destination_type;
  const should_use_existing_destination_config = !!destination && destination.type === destination_type;
  const t2 = `${destination?.id || "new"}:${destination_type}`;
  let t3;
  if ($[0] !== destination) {
    t3 = resource_details_form_props.getInitialValues(destination || undefined);
    $[0] = destination;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== destination || $[3] !== destination_type || $[4] !== should_use_existing_destination_config) {
    t4 = configuration_form_props.getInitialValues(should_use_existing_destination_config ? destination || undefined : undefined, destination_type);
    $[2] = destination;
    $[3] = destination_type;
    $[4] = should_use_existing_destination_config;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = {
      ...t3,
      ...t4
    };
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== HookdeckAPI.destinations || $[10] !== destination) {
    t6 = async values => cleanseFormErrorObject({
      ...(await resource_details_form_props.validate(values, name => destination && name === destination.name ? Promise.resolve(false) : HookdeckAPI.destinations.nameIsUsed(name), true)),
      ...(await configuration_form_props.validate(values as DestinationConfigurationFormValues))
    });
    $[9] = HookdeckAPI.destinations;
    $[10] = destination;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== HookdeckAPI.destinations || $[13] !== addToast || $[14] !== destination || $[15] !== mutateContext || $[16] !== nextStep) {
    t7 = (v, t8) => {
      const {
        resetForm
      } = t8;
      const values_0 = {
        ...resource_details_form_props.postprocessValues(v),
        ...configuration_form_props.postprocessValues(v as never)
      };
      const promise = destination ? HookdeckAPI.destinations.update(destination.id, values_0) : HookdeckAPI.destinations.create(values_0);
      return promise.then(new_destination => {
        if (destination) {
          addToast("success", `Destination ${new_destination.name} configuration updated`);
        }
        resetForm({
          values: {
            ...resource_details_form_props.getInitialValues(new_destination || undefined),
            ...configuration_form_props.getInitialValues(new_destination || undefined),
            show_advanced: v.show_advanced || false
          }
        });
        mutateContext({
          destination: new_destination,
          selected_destination_type: new_destination.type as DestinationType,
          preview_destination_type: new_destination.type as DestinationType,
          destination_type_committed: true
        });
        nextStep();
      }).catch(e => {
        addToast("error", `An error occurred while saving the destination${e.response?.data[0] ? `: ${e.response.data[0]}` : ""}`);
      });
    };
    $[12] = HookdeckAPI.destinations;
    $[13] = addToast;
    $[14] = destination;
    $[15] = mutateContext;
    $[16] = nextStep;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== destination || $[19] !== nextStep) {
    t8 = t9 => {
      const {
        isValid,
        isSubmitting,
        handleSubmit,
        dirty,
        values: values_1
      } = t9;
      return <Form data-testid="guided-add-destination-form" onSubmit={e_0 => {
        if (destination && !dirty && isValid && values_1.type === destination.type) {
          e_0.preventDefault();
          nextStep();
          return;
        }
        handleSubmit(e_0);
      }}><Div m={{
          t: 0
        }}><Div w={{
            px: GUIDED_CONNECTION_WIDTHS.step_container
          }}><configuration_form_props.Fields prefix="" hide_max_delivery_rate={true} hide_destination_type={true} /></Div></Div><Div flex={{
          gap: 3
        }} m={{
          t: 6
        }}><Button submit={true} disabled={!isValid || isSubmitting} primary={!destination} outline={!!destination} icon={isSubmitting ? "loading" : undefined}>{destination ? dirty ? "Update" : "Continue" : "Next"}</Button></Div></Form>;
    };
    $[18] = destination;
    $[19] = nextStep;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t2 || $[22] !== t5 || $[23] !== t6 || $[24] !== t7 || $[25] !== t8) {
    t9 = <Formik key={t2} initialValues={t5} validateOnMount={true} validate={t6} onSubmit={t7}>{t8}</Formik>;
    $[21] = t2;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
};
export default AddDestination;
function _temp(t0, index) {
  const {
    icon,
    text
  } = t0;
  return <Text key={index} dark={true} size="m" flex={{
    align: "center",
    gap: 2
  }}><Icon icon={icon} primary={true} />{text}</Text>;
}